import type { PublicSnapshot } from '../../core/types.ts';
import { pct, usd } from '../lib/format.ts';
import { Panel } from './ui.tsx';

function Step({
  n,
  title,
  body,
  amount,
  tone = 'text-muted',
}: {
  n: string;
  title: string;
  body: string;
  amount?: string;
  tone?: string;
}) {
  return (
    <li className="relative flex gap-3 pb-4 last:pb-0">
      <span className="absolute top-7 bottom-0 left-[13px] w-px bg-edge last:hidden" aria-hidden />
      <span className="z-10 grid size-7 shrink-0 place-items-center rounded-full border border-edge-bright bg-panel-2 text-[11px] font-semibold text-muted">
        {n}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <h3 className="text-[13px] font-semibold">{title}</h3>
          {amount && <span className={`num text-[13px] font-semibold ${tone}`}>{amount}</span>}
        </div>
        <p className="mt-0.5 text-[11.5px] leading-relaxed text-dim">{body}</p>
      </div>
    </li>
  );
}

export function FlowExplainer({ snap }: { snap: PublicSnapshot | null }) {
  const burnShare = snap ? snap.strategy.profitSplit.burn : 0.25;
  const redeployShare = snap ? snap.strategy.profitSplit.redeploy : 0.75;
  const c = snap?.combined;
  const redeployed =
    (snap?.funds.short.lifetime.redeployedUsd ?? 0) + (snap?.funds.long.lifetime.redeployedUsd ?? 0);

  return (
    <Panel title="How the machine works" subtitle="The same loop runs on both coins, in opposite directions.">
      <ol className="mb-4">
        <Step
          n="1"
          title="Trading fees accrue to each coin's creator"
          body="Every buy and sell of $SHORT and $LONG on pump.fun pays a creator fee. The engine sweeps both wallets on every tick."
          amount={c ? usd(c.creatorFeesClaimedUsd) : undefined}
        />
        <Step
          n="2"
          title="Fees fund leveraged positions"
          body="Shortcoin's fees open leveraged shorts on BTC, ETH and SOL. Longcoin's fees open leveraged longs on the same three. 70% of each treasury works, 30% stays behind it as a buffer."
          amount={c ? usd(c.equityUsd) : undefined}
        />
        <Step
          n="3"
          title="Positions exit on a resting trigger"
          body="Take-profit and stop-loss orders sit on the exchange from the moment a position opens. Once a trade is most of the way to target the stop moves to breakeven, to reduce risk; fees and execution slippage still apply."
          amount={c ? `${c.wins}W / ${c.losses}L` : undefined}
        />
        <Step
          n={`${Math.round(burnShare * 100)}%`}
          title="A quarter of new realized profit is burned"
          body="Profit above the fund's previous cumulative high buys its own token and burns it. Recovering earlier losses does not create new burn credit."
          amount={c ? usd(c.burnedUsd) : undefined}
          tone="text-burn"
        />
        <Step
          n={`${Math.round(redeployShare * 100)}%`}
          title="The rest goes straight back to work"
          body="The remaining profit returns to the trading pool, so position sizes compound as the strategy wins."
          amount={usd(redeployed)}
          tone="text-up"
        />
      </ol>

      <div className="flex overflow-hidden rounded-lg border border-edge">
        <div
          className="flex items-center justify-center bg-burn/15 py-2.5 text-[11px] font-semibold text-burn"
          style={{ width: `${burnShare * 100}%` }}
        >
          {pct(burnShare, 0)} burn
        </div>
        <div
          className="flex items-center justify-center bg-up/12 py-2.5 text-[11px] font-semibold text-up"
          style={{ width: `${redeployShare * 100}%` }}
        >
          {pct(redeployShare, 0)} back into the portfolio
        </div>
      </div>
    </Panel>
  );
}
